import {
  collection,
  doc,
  getDocs,
  getCountFromServer,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  Timestamp,
  onSnapshot,
  Unsubscribe
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Notification, NotificationFilter, NotificationStats } from '../types/notification';

export class NotificationService {
  /**
   * Applied whenever a caller does not specify one. An agent's notification
   * history grows without bound; nothing in the UI renders more than a page of
   * them, so an unbounded read is pure download cost.
   */
  private static readonly DEFAULT_LIMIT = 100;

  // Get all notifications for an agent
  static async getNotifications(agentId: string, filter?: NotificationFilter): Promise<Notification[]> {
    try {
      let q = query(
        collection(db, 'notifications'),
        where('agentId', '==', agentId),
        orderBy('createdAt', 'desc')
      );

      // Apply filters
      if (filter?.read !== undefined) {
        q = query(q, where('read', '==', filter.read));
      }
      if (filter?.type) {
        q = query(q, where('type', '==', filter.type));
      }
      q = query(q, firestoreLimit(filter?.limit ?? this.DEFAULT_LIMIT));

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate?.() || new Date(),
          updatedAt: data.updatedAt?.toDate?.()
        } as Notification;
      });
    } catch (error) {
      throw error;
    }
  }

  // Mark notification as read
  static async markAsRead(notificationId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        updatedAt: Timestamp.now()
      });
    } catch (error) {
      throw error;
    }
  }

  // Mark multiple notifications as read
  static async markMultipleAsRead(notificationIds: string[]): Promise<void> {
    try {
      const promises = notificationIds.map(id => 
        updateDoc(doc(db, 'notifications', id), {
          read: true,
          updatedAt: Timestamp.now()
        })
      );
      await Promise.all(promises);
    } catch (error) {
      throw error;
    }
  }

  // Delete a notification
  static async deleteNotification(notificationId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, 'notifications', notificationId));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Notification counts for an agent.
   *
   * Uses server-side aggregation - the previous implementation downloaded every
   * notification the agent had ever received just to derive an unread badge
   * count.
   */
  static async getNotificationStats(agentId: string): Promise<NotificationStats> {
    try {
      const agentNotifications = query(
        collection(db, 'notifications'),
        where('agentId', '==', agentId)
      );

      const [totalSnapshot, unreadSnapshot] = await Promise.all([
        getCountFromServer(agentNotifications),
        getCountFromServer(query(agentNotifications, where('read', '==', false)))
      ]);

      return {
        total: totalSnapshot.data().count,
        unread: unreadSnapshot.data().count
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Subscribe to real-time notifications.
   *
   * @param onError - invoked if the listener is torn down by Firestore (missing
   *   composite index, rules denial, ...). Without it a failed listener goes
   *   silent forever and the UI shows an empty list with no explanation.
   */
  static subscribeToNotifications(
    agentId: string,
    callback: (notifications: Notification[]) => void,
    filter?: NotificationFilter,
    onError?: (error: Error) => void
  ): Unsubscribe {
    let q = query(
      collection(db, 'notifications'),
      where('agentId', '==', agentId),
      orderBy('createdAt', 'desc')
    );

    if (filter?.read !== undefined) {
      q = query(q, where('read', '==', filter.read));
    }
    if (filter?.type) {
      q = query(q, where('type', '==', filter.type));
    }
    q = query(q, firestoreLimit(filter?.limit ?? this.DEFAULT_LIMIT));

    return onSnapshot(
      q,
      (snapshot) => {
        const notifications = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.()
          } as Notification;
        });
        callback(notifications);
      },
      (error) => {
        // Firestore drops the listener on error; it will not recover on its own.
        console.error('NotificationService: notification listener failed:', error);
        onError?.(error);
      }
    );
  }
}